<template>
  <main class="gs-app gm-studio">
    <gs-card class="gm-header gm-page-header">
      <div>
        <p class="gm-eyebrow">GrooveMail</p>
        <h1>Groove forms</h1>
        <p class="gm-muted">Build a form, connect your lists and publish it to GroovePages.</p>
      </div>
      <gs-button @click="gallery = true"
        ><gs-icon name="plus" /> New form</gs-button
      >
    </gs-card>
    <forms-nav />
    <div class="gm-notice">
      Looking for a form you already made?
      <router-link to="/forms">Open Groove forms legacy</router-link>. Your
      existing forms haven’t moved or changed.
    </div>
    <div v-if="error" class="gm-error" role="alert">
      {{ error }}
      <gs-button variant="outline" @click="load">Try again</gs-button>
    </div>
    <div class="gm-toolbar">
      <input
        v-model="search"
        class="gm-search"
        type="search"
        placeholder="Search your forms"
        aria-label="Search your forms"
      /><select v-model="status" aria-label="Filter forms by status"
        ><option value="">All statuses</option
        ><option>draft</option
        ><option>published</option
        ><option>archived</option></select
      >
    </div>
    <p v-if="loading" role="status">Loading your forms…</p>
    <div v-else-if="filtered.length" class="gm-card gm-table-wrap">
      <table class="gm-table">
        <thead>
          <tr>
            <th>Form</th>
            <th>Status</th>
            <th>Submissions</th>
            <th>Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="form in filtered" :key="form.id">
            <td>
              <router-link :to="'/forms/studio/' + form.id">{{
                form.name
              }}</router-link>
            </td>
            <td>
              <span class="gm-status" :class="'gm-status--' + form.status">{{
                form.status
              }}</span>
            </td>
            <td>{{ form.submissions }}</td>
            <td>{{ date(form.updated_at) }}</td>
            <td>
              <div class="gm-actions">
                <gs-button
                  variant="ghost"
                  size="icon"
                  :aria-label="'Edit ' + form.name"
                  @click="$router.push('/forms/studio/' + form.id)"
                  ><gs-icon name="edit"/></gs-button
                ><gs-button
                  variant="ghost"
                  size="icon"
                  :aria-label="'Duplicate ' + form.name"
                  :disabled="busy"
                  @click="duplicate(form)"
                  ><gs-icon name="copy"
                /></gs-button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else-if="!error" class="gm-panel gm-empty">
      <h2>
        {{
          search || status ? 'No matching forms' : 'Start your next connection'
        }}
      </h2>
      <p class="gm-muted">
        {{
          search || status
            ? 'Try another search or status.'
            : 'Choose from 25 ready-to-customize designs, or start with a blank form.'
        }}
      </p>
      <gs-button
        v-if="!search && !status"
        variant="outline"
        @click="gallery = true"
        >Browse form templates</gs-button
      >
    </div>
    <gs-modal
      v-model="gallery"
      title="Start with a form template"
      subtitle="Choose a design. Make it yours."
      size="xl"
    >
      <div class="gm-toolbar">
        <input
          v-model="templateSearch"
          class="gm-search"
          placeholder="Find a template"
          aria-label="Find a template"
        /><select v-model="category" aria-label="Template category"
          ><option value="">All categories</option
          ><option v-for="item in categories" :key="item">{{
            item
          }}</option></select
        >
      </div>
      <div class="gm-grid">
        <article
          v-for="template in filteredTemplates"
          :key="template.id"
          class="gm-card"
        >
          <div
            class="gm-card-preview"
            role="button"
            tabindex="0"
            :aria-label="'Use ' + template.name"
            @click="choose(template)"
            @keydown.enter="choose(template)"
            @keydown.space.prevent="choose(template)"
          >
            <form-canvas
              aria-hidden="true"
              :design="design(template)"
              :fields="template.fields"
            />
          </div>
          <div class="gm-card-content">
            <small>{{ template.category }}</small>
            <h3>{{ template.name }}</h3>
            <p class="gm-muted">{{ template.tagline }}</p>
            <gs-button variant="outline" @click="choose(template)"
              >Use template</gs-button
            >
          </div>
        </article>
      </div>
    </gs-modal>
  </main>
</template>
<script>
import './index';
import axios from 'axios';
import FormsNav from './FormsNav.vue';
import FormCanvas from './FormCanvas.vue';
import templates from './data/form-templates.json';
import { newForm, errorMessage } from './contracts';
export default {
  middleware: 'auth',
  layout: 'defaultWithoutVuePerfectScrollbar',
  components: { FormsNav, FormCanvas },
  data: () => ({
    forms: [],
    templates,
    search: '',
    status: '',
    gallery: false,
    templateSearch: '',
    category: '',
    error: '',
    loading: true,
    busy: false
  }),
  computed: {
    filtered() {
      return this.forms.filter(
        form =>
          (!this.status || form.status === this.status) &&
          form.name.toLowerCase().includes(this.search.toLowerCase())
      );
    },
    categories() {
      return [...new Set(templates.map(template => template.category))];
    },
    filteredTemplates() {
      return templates.filter(
        template =>
          (!this.category || template.category === this.category) &&
          (template.name + template.tagline)
            .toLowerCase()
            .includes(this.templateSearch.toLowerCase())
      );
    }
  },
  created() {
    this.load();
  },
  methods: {
    design(template) {
      return newForm(template).design;
    },
    date(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
    },
    choose(template) {
      this.$router.push({
        path: '/forms/studio/new',
        query: { template: template.id }
      });
    },
    async load() {
      this.error = '';
      this.loading = true;
      try {
        this.forms = (await axios.get('/studio/forms')).data.data;
      } catch (error) {
        this.error = errorMessage(error);
      } finally {
        this.loading = false;
      }
    },
    async duplicate(form) {
      this.busy = true;
      try {
        const source = (await axios.get('/studio/forms/' + form.id)).data;
        const copy = (
          await axios.post('/studio/forms', {
            name: source.name + ' (copy)',
            status: 'draft',
            document: source.document
          })
        ).data;
        this.$router.push('/forms/studio/' + copy.id);
      } catch (error) {
        this.error = errorMessage(error);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
